/**
 * Single registry of every localStorage key written by a `persist(...)` store.
 *
 * Signing out used to clear an ad-hoc list of two entries (`access_token` /
 * `refresh_token`), so `ai-chat-storage` — conversation titles AND full message
 * bodies — survived logout and rendered for whoever signed in next (#1923).
 * A hand-maintained clear list is exactly how that happened, so this module is
 * the single source of truth: every `persist(...)` call takes its `name` from
 * `PERSISTED_STORE_KEYS`, and `persistedKeys.test.ts` fails the build when a
 * store persists under a key that is not registered here.
 */

export const PERSISTED_STORE_KEYS = {
  auth: 'auth-storage',
  aiChat: 'ai-chat-storage',
  mediaUpload: 'media-upload-storage',
} as const

export type PersistedStoreKey = (typeof PERSISTED_STORE_KEYS)[keyof typeof PERSISTED_STORE_KEYS]

/** Every registered key, in declaration order. */
export const ALL_PERSISTED_STORE_KEYS: readonly PersistedStoreKey[] =
  Object.values(PERSISTED_STORE_KEYS)

type ResetFn = () => void

const resetters = new Map<PersistedStoreKey, ResetFn>()

/**
 * Bind a persisted store's in-memory reset to its storage key.
 *
 * Removing the localStorage entry alone is not enough. The store object stays
 * loaded with the previous account's data, React keeps rendering it, and the
 * very next `set()` writes the key straight back. Stores call this once at
 * module scope, immediately after `create(...)`.
 */
export function registerPersistedStore(key: PersistedStoreKey, reset: ResetFn): void {
  resetters.set(key, reset)
}

/** Keys that have called `registerPersistedStore` — used by the AC5 test. */
export function registeredPersistedStoreKeys(): PersistedStoreKey[] {
  return [...resetters.keys()]
}

export interface ClearPersistedStoresOptions {
  /**
   * Keys to leave untouched. Business switching passes `auth-storage`: it has
   * just repointed the cached user at the new business and must not sign the
   * user out.
   */
  except?: readonly PersistedStoreKey[]
}

/**
 * Reset and erase every persisted store.
 *
 * Order is load-bearing: reset the in-memory state FIRST (that write lands in
 * localStorage), then remove the key. Removing first lets zustand's persist
 * middleware re-create the entry from the state it is still holding.
 */
export function clearPersistedStores(options: ClearPersistedStoresOptions = {}): void {
  const except = new Set<PersistedStoreKey>(options.except ?? [])

  for (const key of ALL_PERSISTED_STORE_KEYS) {
    if (except.has(key)) continue
    resetters.get(key)?.()
    localStorage.removeItem(key)
  }
}
